import { useState, useEffect, useRef } from 'react';
import { fetchWeatherData } from '../services/apiService/weatherApi';

const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

/**
 * Custom hook to fetch and manage weather data for a location.
 * Follows the same coordinate-change pattern as usePrayerTimes.
 *
 * @param {Object} coordinates - { lat, lng, name }
 * @returns {{ weatherData: Object|null, weatherLoading: boolean, weatherError: string|null }}
 */
const useWeather = (coordinates) => {
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(null);

  const loadedCoordsRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!coordinates || !coordinates.lat || !coordinates.lng) return;

    // Detect coordinate change (rounded to 2dp — same granularity as cache key)
    const coordKey = `${coordinates.lat.toFixed(2)},${coordinates.lng.toFixed(2)}`;
    const isSame = loadedCoordsRef.current === coordKey;

    const load = async (showSpinner = true) => {
      if (showSpinner) setWeatherLoading(true);
      setWeatherError(null);

      try {
        const data = await fetchWeatherData(coordinates.lat, coordinates.lng);
        if (data) {
          setWeatherData(data);
        } else {
          setWeatherError('Weather data unavailable');
        }
      } catch (err) {
        console.error('useWeather error:', err);
        setWeatherError(err.message || 'Failed to load weather');
      } finally {
        setWeatherLoading(false);
      }
    };

    // On coordinate change — full load with spinner
    if (!isSame) {
      loadedCoordsRef.current = coordKey;
      load(true);
    }

    // Set up auto-refresh (silent — no spinner)
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => load(false), REFRESH_INTERVAL);

    return () => clearInterval(intervalRef.current);
  }, [coordinates]);

  return { weatherData, weatherLoading, weatherError };
};

export default useWeather;
